import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Box,
    List,
    ListItem,
    Typography,
    FormControlLabel,
} from '@material-ui/core';
import i18n from 'i18n-js';
import { OrderItem } from 'store/ordersStore/reducer';
import { useStyles } from '../styles';
import { useAppSelector } from 'index';
import restaurantDetailsStore from 'store/restaurantDetailsStore';
import { useDispatch } from 'react-redux';
import { StornoOrderItemModel } from 'api/models';
import ordersStore from 'store/ordersStore';
import { OrderItemStatus } from 'constants/enums';
import { theme } from 'config/theme';

interface StornoItemsModalProps {
    open: boolean;
    onClose: () => void;
    orderItems: OrderItem[];
    orderId: string;
}

interface SelectedItemData {
    id: string;
    amount: number;
    maxAmount: number;
    reasonId: string;
    parentId?: string;
    isAutoSelected?: boolean;
}

export default function StornoModal({ open, onClose, orderItems, orderId }: StornoItemsModalProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const stornoReasons = useAppSelector((state) => state.restaurantDetails.stornoReasons);
    const [selectedItems, setSelectedItems] = useState<SelectedItemData[]>([]);
    const [hasChanged, setHasChanged] = useState(false);

    const unpaidItems = orderItems.filter(
        (item) => !item.isPaid && item.status !== OrderItemStatus.Storno
    );

    useEffect(() => {
        if (open) {
            setSelectedItems([]);
            setHasChanged(false);
        }
    }, [open]);

    useEffect(() => {
        !!!stornoReasons?.length &&
            dispatch(restaurantDetailsStore.actionCreators.getStornoReasons());
    }, []);

    function handleItemSelection(itemId: string, checked: boolean, parentId?: string) {
        if (checked) {
            const orderItem = unpaidItems.find((item) => (item.uniqueId || item.id) === itemId);
            const dish = parentId
                ? unpaidItems
                      .find((item) => (item.uniqueId || item.id) === parentId)
                      ?.dishes?.find((d) => (d.uniqueId || d.id) === itemId)
                : null;

            const targetItem = dish || orderItem;

            if (targetItem) {
                setSelectedItems((prev) => [
                    ...prev,
                    {
                        id: itemId,
                        amount: 1,
                        maxAmount: targetItem.amount,
                        reasonId: '',
                        parentId,
                        isAutoSelected: false,
                    },
                ]);
            }
        } else {
            const parentItem = unpaidItems.find((item) => (item.uniqueId || item.id) === itemId);
            const hasDishes = parentItem?.dishes && parentItem.dishes.length > 0;

            if (hasDishes) {
                setSelectedItems((prev) =>
                    prev.filter((item) => item.id !== itemId && item.parentId !== itemId)
                );
            } else {
                setSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
            }
        }
        setHasChanged(true);
    }

    function handleAmountChange(itemId: string, delta: number) {
        setSelectedItems((prev) =>
            prev.map((item) => {
                if (item.id === itemId) {
                    const newAmount = Math.max(1, Math.min(item.maxAmount, item.amount + delta));
                    return { ...item, amount: newAmount };
                }
                return item;
            })
        );
        setHasChanged(true);
    }

    function handleReasonChange(itemId: string, reasonId: string) {
        setSelectedItems((prev) =>
            prev.map((item) => {
                if (item.id === itemId) {
                    return { ...item, reasonId };
                }
                return item;
            })
        );
        setHasChanged(true);
    }

    function handleSave() {
        console.log(selectedItems);
        const result: StornoOrderItemModel[] = selectedItems
            .filter((item) => item.parentId)
            .map((item) => ({
                id: item.id,
                menuId: item.parentId || '',
                reasonId: item.reasonId,
            }));

        const standaloneItems: StornoOrderItemModel[] = selectedItems
            .filter((item) => !item.parentId && !hasSubDishes(item.id))
            .map((item) => ({
                id: item.id,
                menuId: '',
                reasonId: item.reasonId,
            }));

        const parentItemsWithoutChildren: StornoOrderItemModel[] = selectedItems
            .filter(
                (item) =>
                    !item.parentId &&
                    hasSubDishes(item.id) &&
                    !hasSelectedChildren(item.id) &&
                    item.reasonId !== ''
            )
            .map((item) => ({
                id: item.id,
                menuId: '',
                reasonId: item.reasonId,
            }));

        const allItems = [...result, ...standaloneItems, ...parentItemsWithoutChildren];
        dispatch(ordersStore.actionCreators.StornoOrderItems(orderId, allItems));
        onClose();
    }

    function handleCancel() {
        onClose();
    }

    function hasSubDishes(itemId: string): boolean {
        const item = unpaidItems.find((item) => (item.uniqueId || item.id) === itemId);
        return !!(item?.dishes && item.dishes.length > 0);
    }

    function hasSelectedChildren(parentId: string): boolean {
        return selectedItems.some((item) => item.parentId === parentId);
    }

    function getItemDisplayName(item: OrderItem): string {
        let displayName = item.name;
        if (item.amount > 1) {
            displayName += ` (x${item.amount})`;
        }
        if (item.size) {
            displayName += ` - ${item.size}`;
        }
        return displayName;
    }

    function getSelectedItemAmount(itemId: string): number {
        const selectedItem = selectedItems.find((si) => si.id === itemId);
        return selectedItem ? selectedItem.amount : 0;
    }

    function getSelectedItemReason(itemId: string): string {
        const selectedItem = selectedItems.find((si) => si.id === itemId);
        return selectedItem ? selectedItem.reasonId : '';
    }

    function isItemSelected(itemId: string): boolean {
        return selectedItems.some((si) => si.id === itemId);
    }

    function isItemAutoSelected(itemId: string): boolean {
        const selectedItem = selectedItems.find((si) => si.id === itemId);
        return selectedItem?.isAutoSelected || false;
    }

    function getParentReason(parentId: string): string {
        const parentItem = selectedItems.find((si) => si.id === parentId);
        return parentItem?.reasonId || '';
    }

    function isValidSelection(): boolean {
        if (selectedItems.length === 0) return false;

        return selectedItems.every((item) => {
            if (item.parentId) {
                return item.reasonId !== '';
            }

            const hasChildren = hasSelectedChildren(item.id);
            if (hasChildren) {
                return true;
            }

            return item.reasonId !== '';
        });
    }

    const isSaveEnabled = hasChanged && isValidSelection();

    function renderDishItem(dish: OrderItem, parentId: string, isSubItem = false) {
        const dishId = dish.uniqueId || dish.id;
        const isSelected = isItemSelected(dishId);
        const isAutoSelected = isItemAutoSelected(dishId);
        const selectedAmount = getSelectedItemAmount(dishId);
        const selectedReason = getSelectedItemReason(dishId);

        return (
            <ListItem
                key={dishId}
                className={classes.stornoModalItemsList}
                style={{
                    paddingLeft: isSubItem ? 40 : 0,
                    borderBottomColor: isSubItem
                        ? theme.palette.grey[300]
                        : theme.palette.grey[200],
                    backgroundColor: isSubItem ? theme.palette.grey[100] : 'transparent',
                    opacity: isAutoSelected ? 0.7 : 1,
                }}
            >
                <Box
                    className={classes.flexRowSpaceBetween}
                    style={{
                        width: '100%',
                    }}
                >
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flex: 1,
                        }}
                    >
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isSelected}
                                    onChange={(e) =>
                                        handleItemSelection(dishId, e.target.checked, parentId)
                                    }
                                    disabled={isAutoSelected}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body1">
                                        {getItemDisplayName(dish)}{' '}
                                        {isSelected && `X ${selectedAmount}`}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        CHF {dish.price.toFixed(2)}
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>

                    {isSelected && !isAutoSelected && (
                        <FormControl style={{ flex: 1, marginLeft: 16 }}>
                            <InputLabel variant="filled">
                                {i18n.t('common.stornoReason')}
                            </InputLabel>
                            <Select
                                value={selectedReason}
                                onChange={(e) =>
                                    handleReasonChange(dishId, e.target.value as string)
                                }
                                variant="filled"
                                fullWidth
                            >
                                {stornoReasons.map((reason) => (
                                    <MenuItem key={reason.id} value={reason.id}>
                                        {reason.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Box>
            </ListItem>
        );
    }

    function renderMainItem(item: OrderItem) {
        const itemId = item.uniqueId || item.id;
        const isSelected = isItemSelected(itemId);
        const selectedAmount = getSelectedItemAmount(itemId);
        const selectedReason = getSelectedItemReason(itemId);
        const hasDishes = item.dishes && item.dishes.length > 0;
        const hasChildren = hasSelectedChildren(itemId);
        const shouldDisableParentReason = hasDishes && hasChildren;

        return (
            <Box key={itemId}>
                <ListItem className={classes.stornoModalItemsList}>
                    <Box
                        className={classes.flexRowSpaceBetween}
                        style={{
                            width: '100%',
                        }}
                    >
                        <Box
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                flex: 1,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={(e) =>
                                            handleItemSelection(itemId, e.target.checked)
                                        }
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography variant="body1">
                                            {getItemDisplayName(item)}{' '}
                                            {isSelected && `X ${selectedAmount}`}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            CHF {item.price.toFixed(2)}
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>

                        {isSelected && (
                            <FormControl
                                style={{
                                    flex: 1,
                                    marginLeft: 16,
                                    opacity: shouldDisableParentReason ? 0.5 : 1,
                                }}
                            >
                                <InputLabel variant="filled">
                                    {i18n.t('common.stornoReason')}
                                </InputLabel>
                                <Select
                                    value={shouldDisableParentReason ? '' : selectedReason}
                                    onChange={(e) =>
                                        handleReasonChange(itemId, e.target.value as string)
                                    }
                                    variant="filled"
                                    fullWidth
                                    disabled={shouldDisableParentReason}
                                >
                                    {stornoReasons.map((reason) => (
                                        <MenuItem key={reason.id} value={reason.id}>
                                            {reason.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                </ListItem>

                {isSelected && hasDishes && (
                    <Box>
                        <Typography variant="body2" className={classes.stornoModalMenuDishesTitle}>
                            {i18n.t('common.selectDishes')}
                        </Typography>
                        {item
                            .dishes!.filter((d) => d.status !== OrderItemStatus.Storno)
                            .map((dish) => renderDishItem(dish, itemId, true))}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{i18n.t('common.selectItemsForStorno')}</DialogTitle>
            <DialogContent>
                {unpaidItems.length > 0 ? (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            {i18n.t('common.selectItems')}
                        </Typography>
                        <List>{unpaidItems.map((item) => renderMainItem(item))}</List>
                    </Box>
                ) : (
                    <Box textAlign="center" py={3}>
                        {i18n.t('common.noItemsForStorno')}
                    </Box>
                )}
                <DialogActions>
                    <Button onClick={handleCancel} color="primary">
                        {i18n.t('button.cancel')}
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={!isSaveEnabled}
                    >
                        {i18n.t('button.save')}
                    </Button>
                </DialogActions>
            </DialogContent>
        </Dialog>
    );
}
